<script setup lang="ts">
import type { EventItemProps } from '@/types/appTypes'

const props = withDefaults(defineProps<EventItemProps>(), {
  showLabel: false,
  fromname: '',
})

const app = useNuxtApp()
const storeIndex = useStoreIndex()

const place = app.$getEventPlace(props.item) // 場所
const periodLabel = app.$getEventPeriodLabel(props.item)
const isJyunkai = app.$getArticleRelationsSingle(props.item, 'jyunkai', null)

const linkTo = computed((): object => {
  if (props.fromname) {
    return {
      name: 'article-slug',
      params: {
        article: props.item.article_type_slug,
        slug: props.item.article_slug,
      },
      query: { fromname: props.fromname },
    }
  } else {
    return {
      name: 'article-slug',
      params: {
        article: props.item.article_type_slug,
        slug: props.item.article_slug,
      },
    }
  }
})
</script>

<template>
  <div
    class="c-eventItem"
    v-bind:class="{ 'is-event': showLabel && item.custom && item.custom.is_event === '1' }"
  >
    <div
      class="c-eventItem_image"
    >
      <NuxtLink
        class="u-hoverZoom"
        v-bind:to="linkTo"
      >
        <CommonImage
          v-bind:src="storeIndex.getArticleEyeCatchImageURL(item.eye_catch_image_object)"
          error-src="/images/noimage.svg"
          v-bind:alt="item.title"
          height="280"
          object-fit="contain"
          is-hover-effect
        />
      </NuxtLink>
    </div>

    <div class="c-eventItem_blockExpand">
      <Clip
        class="c-eventItem_blockExpand_clip"
        v-bind:article-type-slug="item.article_type_slug"
        v-bind:article-slug="item.article_slug"
        v-bind:count="item.user_favorite_count"
      />
      <div class="c-eventItem_museum c-clampLines is-clampline3">
        <NuxtLink
          v-bind:to="linkTo"
        >
          {{ item.title }}
        </NuxtLink>
      </div>
      <div
        class="c-eventItem_event"
      >
        {{ place }}
      </div>
    </div>

    <div class="c-eventItem_blockFixed">
      <div class="c-eventItem_duration">
        {{ periodLabel }}
      </div>
      <PeriodRemain
        v-bind:term-from="item.term_from"
        v-bind:term-to="item.term_to"
        v-bind:hide-day-count="item.day_count_display !== 1"
        small
      />
    </div>
    <div
      v-if="showLabel && isJyunkai"
      class="c-eventItem_label c-eventItem_label_jyunkai"
    >
      巡回展
    </div>
    <div
      v-else-if="showLabel && item.custom && item.custom.is_event === '1'"
      class="c-eventItem_label"
    >
      イベント
    </div>
  </div>
</template>

<style lang="scss" scoped>
@use "@/assets/css/foundation/mixin/responsive";

.c-eventItem {
  display: flex;
  position: relative;
  flex-direction: column;
  width: 346px;
  min-height: 430px;
  margin-top: 30px;
  border: 1px solid var(--g_color_border_gray);
  font-family: var(--g_font_family_NotoSansCJKjp);
  font-size: 12px;
  font-weight: 400;

  @include responsive.mq_down(sp) {
    width: 100%;
    max-width: 100%;
    margin: 30px auto 0;
    box-shadow: none;
  }

  &_image {
    display: block;
    height: 280px;
  }

  &_blockExpand {
    position: relative;
    flex: 1 1 auto;
    padding: 28px 22px 14px;

    &_clip {
      position: absolute;
      top: -18px;
      right: 0;
    }
  }

  &_blockFixed {
    flex: 0 0 63px;
    padding: 0 22px 18px;
  }

  &_museum {
    font-size: 18px;

    & > div:last-of-type {
      @include responsive.mq_down(sp) {
        flex-basis: 80px;
        width: 80px;
        height: 80px;
        margin-left: 10px;
      }
    }

    a {
      color: #252525;
      font-weight: bold;
    }
  }

  &_event {
    margin-top: 5px;
    font-size: 14px;
    font-weight: bold;
    line-height: 1.5;

    @include responsive.mq_down(sp) {
      margin-top: 13px;
    }
  }

  &_duration {
    margin-bottom: 12px;
    font-size: 14px;
  }

  &_label {
    position: absolute;
    top: 7px;
    right: 6px;
    width: 94px;
    height: 28px;
    border-radius: 14px;
    opacity: 0.8;
    background-color: var(--color_event_label_bg);
    box-shadow: 1px 1.7px 4px 0 rgb(0, 0, 0, 0.06);
    color: #fff;
    font-size: 12px;
    font-weight: bold;
    letter-spacing: 1.2px;
    line-height: 28px;
    text-align: center;

    &_jyunkai {
      background-color: var(--color_jyunkai_author_bg);
    }
  }

  &.is-event {
    border: 1px solid #bbcbdb;
  }
}
</style>
